import { executeTool, TOOL_DEFINITIONS, type ToolResult } from './tools';

type ToolDefinition = (typeof TOOL_DEFINITIONS)[number];
type ToolName = ToolDefinition['name'];

export type QueryRoute = {
  kind: 'ontology-only' | 'operational-only' | 'mixed' | 'external-only';
  allowedToolNames: ToolName[];
  reason: string;
};

const ONTOLOGY_TOOLS: ToolName[] = ['search_wiki_concepts', 'read_wiki_page'];
const OPERATIONAL_TOOLS: ToolName[] = [
  'search_products',
  'search_components',
  'analyze_bom',
  'check_inventory',
  'search_changes',
  'where_used',
  'dashboard_summary',
  'entity_graph',
  'graph_context',
  'impact_analysis',
  'find_related',
];
const EXTERNAL_TOOLS: ToolName[] = ['exa_web_search'];

const ONTOLOGY_HINTS = [
  'ontology', 'graphwiki', 'roadmap', 'capability pipeline', 'workflow', 'what is', 'define',
  'definition', 'meaning', 'concept', 'relationship', 'entity type', 'entity types', 'guide',
  'how should', 'best practice', 'industry use', 'industry workflow', 'plm process',
  'feature request', 'productivity problem', 'requested feature',
];

const OPERATIONAL_HINTS = [
  'product', 'products', 'component', 'components', 'bom', 'bill of materials', 'inventory',
  'stock', 'document', 'documents', 'change request', 'change requests', 'change order',
  'supplier', 'suppliers', 'where used', 'affected', 'impact', 'prototype', 'production',
  'released', 'revision', 'revisions', 'part number', 'low-stock', 'obsolete', 'availability',
];

const EXTERNAL_HINTS = [
  'datasheet', 'specification sheet', 'standard', 'ipc', 'iec', 'ul', 'pricing', 'price',
  'distributor', 'market availability', 'lead time', 'lifecycle notice', 'pcn', 'supplier notice',
  'web', 'internet', 'search online', 'outside the ontology', 'external', 'google', 'find online',
  'manufacturer site', 'vendor site', 'alternative source',
];

function normalizeContent(content: string) {
  return content.toLowerCase();
}

function containsAny(content: string, hints: string[]) {
  return hints.some((hint) => content.includes(hint));
}

function uniqueToolNames(names: ToolName[]) {
  return Array.from(new Set(names));
}

function latestUserPrompt(messages: ChatMessage[]) {
  const latest = [...messages].reverse().find((message) => message.role === 'user');
  return latest?.content || '';
}

export function routeQueryToTools(content: string): QueryRoute {
  const normalized = normalizeContent(content);
  const hasOntologyIntent = containsAny(normalized, ONTOLOGY_HINTS);
  const hasOperationalIntent = containsAny(normalized, OPERATIONAL_HINTS);
  const hasExternalIntent = containsAny(normalized, EXTERNAL_HINTS);

  if (hasExternalIntent && !hasOntologyIntent && !hasOperationalIntent) {
    return {
      kind: 'external-only',
      allowedToolNames: EXTERNAL_TOOLS,
      reason: 'Question explicitly asks for external evidence outside ontology and live PLM data.',
    };
  }

  if (hasOntologyIntent && !hasOperationalIntent && !hasExternalIntent) {
    return {
      kind: 'ontology-only',
      allowedToolNames: ONTOLOGY_TOOLS,
      reason: 'Question is about ontology meaning, workflow guidance, or roadmap rather than live records.',
    };
  }

  if (hasOntologyIntent || hasOperationalIntent) {
    return {
      kind: hasOntologyIntent && hasOperationalIntent ? 'mixed' : 'operational-only',
      allowedToolNames: uniqueToolNames([
        ...(hasOntologyIntent ? ONTOLOGY_TOOLS : []),
        ...OPERATIONAL_TOOLS,
        ...(hasExternalIntent ? EXTERNAL_TOOLS : []),
      ]),
      reason: hasOntologyIntent && hasOperationalIntent
        ? 'Question combines ontology meaning with live PLM objects or relationships.'
        : 'Question is grounded in live PLM records and graph relationships.',
    };
  }

  return {
    kind: hasExternalIntent ? 'mixed' : 'mixed',
    allowedToolNames: uniqueToolNames([...ONTOLOGY_TOOLS, ...OPERATIONAL_TOOLS, ...(hasExternalIntent ? EXTERNAL_TOOLS : [])]),
    reason: hasExternalIntent
      ? 'Question is ambiguous but explicitly mentions external evidence, so ontology, operational, and external tools remain available.'
      : 'Question is ambiguous, so ontology and operational tools remain available while external search stays disabled by default.',
  };
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  toolCalls?: { name: string; args: Record<string, any>; result?: ToolResult }[];
  timestamp: Date;
}

const BASE_SYSTEM_PROMPT = `You are the ElectroPLM AI Assistant — an expert electronics engineering copilot embedded inside a Product Lifecycle Management system.

You help engineers with:
- Finding and analyzing products, components, and BOMs
- Understanding change requests and their impact
- Checking inventory levels and suggesting alternatives
- Searching the web for datasheets, component specs, and engineering information
- Answering questions about PLM workflows (ECR/ECO process, BOM management, revision control)
- Reasoning over the PLM ontology: products, components, BOMs, documents, change requests, change orders, suppliers, and their relationships

Available tools:
[tool list injected at runtime]

Guidelines:
- Always use tools to fetch real data before answering questions about the system
- Treat the PLM as an ontology graph, not just a set of tables
- For PLM domain explanations, roadmap questions, ontology definitions, or workflow meaning, use search_wiki_concepts and read_wiki_page before answering
- For relationship-heavy questions, prefer this sequence: find_related -> graph_context -> entity_graph -> impact_analysis
- Use search tools first when the user does not know exact IDs or names; use graph tools once a likely entity is identified
- Combine wiki context with live data when the user asks both what something means and how it applies to current records
- When answering, explicitly name the ontology objects involved: product, component, BOM, document, change request, supplier, revision
- Only use exa_web_search for information outside the curated ontology and outside live PLM records
- Do not use exa_web_search for PLM ontology definitions, roadmap questions, workflow meaning, or relationships that should come from GraphWiki or the database
- Use exa_web_search when the user needs external evidence such as datasheets, standards, distributor pricing, market availability, or supplier notices
- If the message is a feature request, analyze it against the current implementation and answer with: problem framing, impacted workflows, proposed capability, dependencies, risks, roadmap placement, and immediate next build step
- Present data in clean, readable format — use tables for lists, bullet points for summaries
- When analyzing BOMs, always mention total cost, component count, and any risk flags
- For where-used analysis, clearly list all affected assemblies
- For impact analysis, separate direct neighbors from second-order effects when the data supports it
- Be concise but thorough — engineers value precision
- If you don't have enough data, say so and suggest what the user should do
- Format part numbers in monospace: \`STM32H743\`
- Format currency as $X,XXX.XX
- When suggesting component alternatives, note key differences in specs`;

function buildSystemPrompt(route: QueryRoute, allowedTools: ToolDefinition[]) {
  return `${BASE_SYSTEM_PROMPT}

Routing decision:
- route: ${route.kind}
- reason: ${route.reason}

Available tools:
${allowedTools.map((tool) => `- ${tool.name}: ${tool.description}`).join('\n')}`;
}

export interface AgentConfig {
  apiKey: string;
  model?: string;
  maxToolCalls?: number;
}

/**
 * Send a message to the AI agent and get a response.
 * Handles tool calling in a loop until the agent produces a final response.
 */
export async function sendMessage(
  messages: ChatMessage[],
  config: AgentConfig
): Promise<{ response: string; toolCalls: ChatMessage['toolCalls'] }> {
  const { apiKey, model = 'claude-sonnet-4-20250514', maxToolCalls = 5 } = config;
  const route = routeQueryToTools(latestUserPrompt(messages));
  const allowedToolDefinitions = TOOL_DEFINITIONS.filter((tool) => route.allowedToolNames.includes(tool.name));
  const systemPrompt = buildSystemPrompt(route, allowedToolDefinitions);

  // Build Anthropic API messages
  const apiMessages = messages
    .filter(m => m.role !== 'system')
    .map(m => ({
      role: m.role === 'tool' ? 'user' as const : m.role as 'user' | 'assistant',
      content: m.role === 'tool'
        ? `[Tool Result]\n${m.content}`
        : m.content,
    }));

  // Build tools for the API
  const tools = allowedToolDefinitions.map(t => ({
    name: t.name,
    description: t.description,
    input_schema: {
      type: 'object' as const,
      properties: Object.fromEntries(
        Object.entries(t.parameters).map(([key, desc]) => [
          key,
          {
            type: (desc as string).startsWith('number') ? 'number' : 'string',
            description: desc,
          },
        ])
      ),
      required: Object.entries(t.parameters)
        .filter(([_, desc]) => !(desc as string).includes('optional'))
        .map(([key]) => key),
    },
  }));

  let allToolCalls: ChatMessage['toolCalls'] = [];
  let iteration = 0;
  let currentMessages = [...apiMessages];

  while (iteration < maxToolCalls) {
    iteration++;

    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model,
        max_tokens: 4096,
        system: systemPrompt,
        messages: currentMessages,
        tools,
      }),
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`API error ${response.status}: ${err}`);
    }

    const data = await response.json();

    // Check if the response contains tool use
    const toolUseBlocks = (data.content || []).filter((b: any) => b.type === 'tool_use');
    const textBlocks = (data.content || []).filter((b: any) => b.type === 'text');

    if (toolUseBlocks.length === 0) {
      // No tool calls — return the text response
      const responseText = textBlocks.map((b: any) => b.text).join('\n');
      return { response: responseText, toolCalls: allToolCalls };
    }

    // Execute tool calls
    const toolResults: { type: string; tool_use_id: string; content: string }[] = [];

    for (const toolBlock of toolUseBlocks) {
      const result = await executeTool(toolBlock.name, toolBlock.input || {});
      allToolCalls = [...(allToolCalls || []), {
        name: toolBlock.name,
        args: toolBlock.input || {},
        result,
      }];
      toolResults.push({
        type: 'tool_result',
        tool_use_id: toolBlock.id,
        content: JSON.stringify(result.result || result.error),
      });
    }

    // Add assistant message with tool use + tool results for next iteration
    currentMessages = [
      ...currentMessages,
      { role: 'assistant' as const, content: data.content },
      { role: 'user' as const, content: toolResults },
    ];
  }

  return { response: 'I reached the maximum number of tool calls. Please try a more specific question.', toolCalls: allToolCalls };
}
